import {
  UserRole,
  AccountTier,
  normalizeAccountTier,
  Permission,
  UserProfile,
  UserProfileRecord,
  ConsultationRecord
} from '../types/user';

/**
 * Centralized Role Permission Mapping
 * Explicit permissions granted to each administrative or client role.
 */
export const ROLE_PERMISSIONS: Record<UserRole, Permission[]> = {
  [UserRole.SUPER_ADMIN]: [
    Permission.VIEW_OWN_PROFILE,
    Permission.EDIT_OWN_PROFILE,
    Permission.DELETE_OWN_PROFILE,
    Permission.VIEW_FAMILY_PROFILE,
    Permission.CREATE_FAMILY_PROFILE,
    Permission.EDIT_FAMILY_PROFILE,
    Permission.DELETE_FAMILY_PROFILE,
    Permission.CREATE_KUNDALI,
    Permission.VIEW_OWN_KUNDALI,
    Permission.VIEW_FAMILY_KUNDALI,
    Permission.PRINT_KUNDALI,
    Permission.SAVE_REPORT,
    Permission.EXPORT_KUNDALI,
    Permission.BASIC_PREDICTIONS,
    Permission.BASIC_AI_QUESTION,
    Permission.DETAILED_AI_ANALYSIS,
    Permission.TECHNICAL_REASONING,
    Permission.ADVANCED_CALCULATIONS,
    Permission.LEARNING_EXPLANATIONS,
    Permission.CONSULTATION_ACCESS,
    Permission.ASSIGN_CONSULTATION,
    Permission.VIEW_ASSIGNED_CUSTOMER,
    Permission.PRO_CALCULATIONS,
    Permission.PRACTICE_PROFILES,
    Permission.USER_ADMINISTRATION,
    Permission.RESET_USER_CREDENTIALS,
    Permission.MANAGE_ROLES,
    Permission.MANAGE_ENTITLEMENTS,
    Permission.KUNDALI_ADMINISTRATION,
    Permission.SYSTEM_CONFIGURATION,
    Permission.AUDIT_LOG_ACCESS,
    Permission.DATA_RECOVERY
  ],

  [UserRole.TESTER]: [
    Permission.VIEW_OWN_PROFILE,
    Permission.EDIT_OWN_PROFILE,
    Permission.DELETE_OWN_PROFILE,
    Permission.VIEW_FAMILY_PROFILE,
    Permission.CREATE_FAMILY_PROFILE,
    Permission.EDIT_FAMILY_PROFILE,
    Permission.CREATE_KUNDALI,
    Permission.VIEW_OWN_KUNDALI,
    Permission.VIEW_FAMILY_KUNDALI,
    Permission.PRINT_KUNDALI,
    Permission.SAVE_REPORT,
    Permission.EXPORT_KUNDALI,
    Permission.BASIC_PREDICTIONS,
    Permission.BASIC_AI_QUESTION,
    Permission.DETAILED_AI_ANALYSIS,
    Permission.TECHNICAL_REASONING,
    Permission.ADVANCED_CALCULATIONS,
    Permission.LEARNING_EXPLANATIONS,
    Permission.CONSULTATION_ACCESS,
    Permission.VIEW_ASSIGNED_CUSTOMER,
    Permission.PRO_CALCULATIONS,
    Permission.PRACTICE_PROFILES,
    Permission.USER_ADMINISTRATION,
    Permission.SYSTEM_CONFIGURATION,
    Permission.AUDIT_LOG_ACCESS,
    Permission.DATA_RECOVERY
  ],

  [UserRole.ADMIN]: [
    // Admin / Super User: user management, status, support workflows
    // Deliberately restricted from Kundali personal data / charts of other users
    Permission.VIEW_OWN_PROFILE,
    Permission.EDIT_OWN_PROFILE,
    Permission.VIEW_FAMILY_PROFILE,
    Permission.CREATE_FAMILY_PROFILE,
    Permission.CREATE_KUNDALI,
    Permission.VIEW_OWN_KUNDALI,
    Permission.VIEW_FAMILY_KUNDALI,
    Permission.PRINT_KUNDALI,
    Permission.BASIC_PREDICTIONS,
    Permission.BASIC_AI_QUESTION,
    Permission.USER_ADMINISTRATION,
    Permission.RESET_USER_CREDENTIALS,
    Permission.SYSTEM_CONFIGURATION,
    Permission.AUDIT_LOG_ACCESS
  ],

  [UserRole.CONSULTING_ASTROLOGER]: [
    Permission.VIEW_OWN_PROFILE,
    Permission.EDIT_OWN_PROFILE,
    Permission.CREATE_KUNDALI,
    Permission.VIEW_OWN_KUNDALI,
    Permission.PRINT_KUNDALI,
    Permission.SAVE_REPORT,
    Permission.BASIC_PREDICTIONS,
    Permission.BASIC_AI_QUESTION,
    Permission.DETAILED_AI_ANALYSIS,
    Permission.TECHNICAL_REASONING,
    Permission.ADVANCED_CALCULATIONS,
    Permission.PRO_CALCULATIONS,
    Permission.CONSULTATION_ACCESS,
    Permission.VIEW_ASSIGNED_CUSTOMER
  ],

  [UserRole.PRO_ASTROLOGER]: [
    Permission.VIEW_OWN_PROFILE,
    Permission.EDIT_OWN_PROFILE,
    Permission.VIEW_FAMILY_PROFILE,
    Permission.CREATE_FAMILY_PROFILE,
    Permission.CREATE_KUNDALI,
    Permission.VIEW_OWN_KUNDALI,
    Permission.VIEW_FAMILY_KUNDALI,
    Permission.PRINT_KUNDALI,
    Permission.SAVE_REPORT,
    Permission.EXPORT_KUNDALI,
    Permission.BASIC_PREDICTIONS,
    Permission.BASIC_AI_QUESTION,
    Permission.DETAILED_AI_ANALYSIS,
    Permission.PRO_CALCULATIONS,
    Permission.ADVANCED_CALCULATIONS
  ],

  [UserRole.NOVICE_ASTROLOGER]: [
    Permission.VIEW_OWN_PROFILE,
    Permission.EDIT_OWN_PROFILE,
    Permission.VIEW_FAMILY_PROFILE,
    Permission.CREATE_FAMILY_PROFILE,
    Permission.CREATE_KUNDALI,
    Permission.VIEW_OWN_KUNDALI,
    Permission.VIEW_FAMILY_KUNDALI,
    Permission.PRINT_KUNDALI,
    Permission.BASIC_PREDICTIONS,
    Permission.BASIC_AI_QUESTION,
    Permission.DETAILED_AI_ANALYSIS,
    Permission.LEARNING_EXPLANATIONS,
    Permission.ADVANCED_CALCULATIONS
  ],

  [UserRole.ADVANCED_ASTROLOGY_USER]: [
    Permission.VIEW_OWN_PROFILE,
    Permission.EDIT_OWN_PROFILE,
    Permission.VIEW_FAMILY_PROFILE,
    Permission.CREATE_FAMILY_PROFILE,
    Permission.CREATE_KUNDALI,
    Permission.VIEW_OWN_KUNDALI,
    Permission.VIEW_FAMILY_KUNDALI,
    Permission.PRINT_KUNDALI,
    Permission.SAVE_REPORT,
    Permission.BASIC_PREDICTIONS,
    Permission.BASIC_AI_QUESTION,
    Permission.DETAILED_AI_ANALYSIS,
    Permission.TECHNICAL_REASONING,
    Permission.ADVANCED_CALCULATIONS
  ],

  [UserRole.BASIC_USER]: [
    Permission.VIEW_OWN_PROFILE,
    Permission.EDIT_OWN_PROFILE,
    Permission.VIEW_FAMILY_PROFILE,
    Permission.CREATE_FAMILY_PROFILE,
    Permission.CREATE_KUNDALI,
    Permission.VIEW_OWN_KUNDALI,
    Permission.VIEW_FAMILY_KUNDALI,
    Permission.PRINT_KUNDALI,
    Permission.BASIC_PREDICTIONS,
    Permission.BASIC_AI_QUESTION
  ],

  [UserRole.GUEST]: [
    Permission.CREATE_KUNDALI,
    Permission.BASIC_PREDICTIONS,
    Permission.BASIC_AI_QUESTION
  ]
};

/**
 * Entitlement-based Additional Permissions
 */
export const TIER_PERMISSIONS: Record<AccountTier, Permission[]> = {
  [AccountTier.GUEST]: [],
  [AccountTier.BASIC]: [Permission.PRINT_KUNDALI, Permission.SAVE_REPORT],
  [AccountTier.PRO_ASTRO]: [
    Permission.PRINT_KUNDALI,
    Permission.SAVE_REPORT,
    Permission.EXPORT_KUNDALI,
    Permission.DETAILED_AI_ANALYSIS,
    Permission.ADVANCED_CALCULATIONS
  ],
  [AccountTier.ACHARYA]: [
    Permission.PRINT_KUNDALI,
    Permission.SAVE_REPORT,
    Permission.EXPORT_KUNDALI,
    Permission.DETAILED_AI_ANALYSIS,
    Permission.TECHNICAL_REASONING,
    Permission.ADVANCED_CALCULATIONS,
    Permission.PRO_CALCULATIONS
  ]
};

/**
 * Resolve all effective permissions for a user
 */
export function getEffectivePermissions(user: UserProfile | null): Set<Permission> {
  const permissions = new Set<Permission>();

  if (!user) {
    // Guest role permissions
    const guestPerms = ROLE_PERMISSIONS[UserRole.GUEST] || [];
    guestPerms.forEach((p) => permissions.add(p));
    return permissions;
  }

  // 1. Add Role permissions
  const rolePerms = ROLE_PERMISSIONS[user.role] || ROLE_PERMISSIONS[UserRole.BASIC_USER];
  rolePerms.forEach((p) => permissions.add(p));

  // 2. Add Account Tier permissions (normalized to canonical 4-tier model)
  const { canonicalTier } = normalizeAccountTier(user.tier);
  const tierPerms = TIER_PERMISSIONS[canonicalTier] || [];
  tierPerms.forEach((p) => permissions.add(p));

  // 3. Add Explicit Practice Entitlement
  if (user.hasPracticeEntitlement) {
    permissions.add(Permission.PRACTICE_PROFILES);
  }

  // 4. Add Explicit Consultation Entitlement
  if (user.hasConsultationEntitlement) {
    permissions.add(Permission.CONSULTATION_ACCESS);
  }

  return permissions;
}

/**
 * Check if user has a specific permission
 */
export function hasPermission(user: UserProfile | null, permission: Permission): boolean {
  const effective = getEffectivePermissions(user);
  return effective.has(permission);
}

export interface AuthorizationDecision {
  allow: boolean;
  reason?: string;
  auditEventType?: string;
}

/**
 * Core Authorization Decision Engine (ALLOW = Authenticated && Role Permits && Entitlement Permits && Ownership/Assignment Permits)
 */
export function evaluateResourceAccess(params: {
  user: UserProfile | null;
  resourceType: 'profile' | 'kundali' | 'consultation' | 'admin_console' | 'practice_chart';
  resourceOwnerId?: string;
  profileId?: string;
  kundaliId?: string;
  consultation?: ConsultationRecord | null;
  requiredPermission?: Permission;
}): AuthorizationDecision {
  const { user, resourceType, resourceOwnerId, consultation, requiredPermission } = params;

  // 1. Permission check if required
  if (requiredPermission && !hasPermission(user, requiredPermission)) {
    return {
      allow: false,
      reason: `User role [${user?.role || 'guest'}] and tier [${user?.tier || 'free'}] lack permission: ${requiredPermission}`
    };
  }

  // 2. Guest checks
  if (!user) {
    if (resourceType === 'admin_console' || resourceType === 'consultation') {
      return { allow: false, reason: 'Authentication required for privileged resource access.' };
    }
    // Guests can generate temporary Kundalis, but cannot persist or read private cloud profiles
    if (resourceOwnerId && resourceOwnerId !== 'guest') {
      return { allow: false, reason: 'Guest cannot access registered user resources.' };
    }
    return { allow: true };
  }

  // 3. Super Admin has authorized administrative access to all maintenance resources
  if (user.role === UserRole.SUPER_ADMIN) {
    return { allow: true };
  }

  // 4. Admin / Super User is deliberately restricted from Kundali / Personal Data of users
  if (user.role === UserRole.ADMIN) {
    if (resourceType === 'kundali' && resourceOwnerId && resourceOwnerId !== user.uid) {
      return {
        allow: false,
        reason: 'Admin role is restricted from accessing private Kundali birth data of other users.'
      };
    }
    if (resourceType === 'admin_console') {
      return { allow: true };
    }
  }

  // 5. Practice Charts: Require explicit practice entitlement or Pro Astrologer role
  if (resourceType === 'practice_chart') {
    if (user.hasPracticeEntitlement || user.role === UserRole.PRO_ASTROLOGER) {
      return { allow: true };
    }
    return {
      allow: false,
      reason: 'Access to Practice Charts requires active Practice Profile entitlement.'
    };
  }

  // 6. Consulting Astrologer: Access ONLY via explicit active consultation assignment
  if (user.role === UserRole.CONSULTING_ASTROLOGER) {
    if (resourceOwnerId === user.uid) {
      return { allow: true }; // Own charts
    }

    if (!consultation) {
      return {
        allow: false,
        reason: 'Consulting Astrologer can only access customer data with an explicit Consultation assignment.'
      };
    }

    // Validate consultation parameters
    if (consultation.consultantAstrologerId !== user.uid) {
      return {
        allow: false,
        reason: 'Consultation is assigned to a different astrologer.'
      };
    }

    if (consultation.status !== 'active') {
      return {
        allow: false,
        reason: `Consultation is ${consultation.status} and no longer active.`
      };
    }

    // (±7-day restriction permanently removed per instructions)

    // Match profile or kundali if provided
    if (params.profileId && consultation.authorizedProfileId !== params.profileId) {
      return {
        allow: false,
        reason: 'Profile requested is outside authorized consultation scope.'
      };
    }

    if (params.kundaliId && consultation.authorizedKundaliId !== params.kundaliId) {
      return {
        allow: false,
        reason: 'Kundali requested is outside authorized consultation scope.'
      };
    }

    return { allow: true };
  }

  // 7. Normal Ownership check for Basic User, Pro Astrologer, Novice, Advanced
  if (resourceOwnerId) {
    if (resourceOwnerId === user.uid) {
      return { allow: true };
    }
    return {
      allow: false,
      reason: `Access Denied: User [${user.uid}] is not the owner of resource [owned by: ${resourceOwnerId}].`
    };
  }

  return { allow: true };
}
